import React, { useState, useCallback, useMemo, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import classnames from 'classnames'
import { t } from 'src/translations'
import { DeleteIcon } from '@toasttab/buffet-pui-icons'

const TEN_MEGA_BYTES = 1024 * 1024 * 10

type Props = {
  id: string
  label?: string
  required?: boolean
  validate?: (value: any) => boolean | string | undefined
  onFileSelect?: (file: File | null) => void
  className?: string
}

const AttachmentField = ({
  id,
  label,
  required,
  validate,
  onFileSelect,
  className
}: Props) => {
  const {
    register,
    watch,
    setValue,
    formState: { errors }
  } = useFormContext()

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadError, setUploadError] = useState<string>('')
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const fieldValue = watch(id)

  const validateFile = useCallback((file: File): string | null => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      return t('form-page.attachment-field.error.pdf-only')
    }
    if (file.size > TEN_MEGA_BYTES) {
      return t('form-page.attachment-field.error.file-too-large')
    }
    return null
  }, [])

  const handleFileSelection = useCallback(
    (file: File | null) => {
      setUploadError('')

      if (!file) {
        setSelectedFile(null)
        setValue(id, null)
        onFileSelect?.(null)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return
      }

      const error = validateFile(file)
      if (error) {
        setUploadError(error)
        setSelectedFile(null)
        setValue(id, null)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return
      }

      setSelectedFile(file)
      setValue(id, {
        file,
        originalName: file.name,
        size: file.size
      })
      onFileSelect?.(file)
    },
    [id, setValue, onFileSelect, validateFile]
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files
      if (files && files.length > 0) {
        handleFileSelection(files[0] || null)
      }
    },
    [handleFileSelection]
  )

  const handleDragEnter = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setIsDragging(false)

      const files = event.dataTransfer.files
      if (files && files.length > 0) {
        handleFileSelection(files[0] || null)
      }
    },
    [handleFileSelection]
  )

  const handleButtonClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleRemoveFile = useCallback(() => {
    handleFileSelection(null)
  }, [handleFileSelection])

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const errorMessage = useMemo(() => {
    return errors[id]?.message || uploadError
  }, [errors, id, uploadError])

  return (
    <div
      className={classnames(
        'inputField formInputContainer attachment-field',
        errors[id] && 'error',
        fieldValue && 'filled',
        className
      )}
    >
      {label && (
        <label htmlFor={id}>
          <span>{label}</span>
          {required && <span className='required'> *</span>}
        </label>
      )}

      <div
        className={classnames('file-drop-zone', {
          dragging: isDragging,
          'has-file': selectedFile
        })}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type='file'
          accept='.pdf'
          hidden
          onChange={handleInputChange}
          data-testid={`${id}-file-input`}
        />

        {!selectedFile ? (
          <div className='file-selector-empty'>
            <button
              type='button'
              className='file-select-button'
              onClick={handleButtonClick}
              data-testid={`${id}-select-button`}
            >
              {t('form-page.attachment-field.btn.select-pdf')}
            </button>
            <p className='help-text'>
              {t('form-page.attachment-field.drag-drop-pdf')}
            </p>
          </div>
        ) : (
          <div className='file-selected' data-testid={`${id}-selected-file`}>
            <div className='file-info'>
              <span className='file-name'>{selectedFile.name}</span>
              <span className='file-size'>
                {formatFileSize(selectedFile.size)}
              </span>
            </div>
            <button
              type='button'
              className='file-remove-button'
              onClick={handleRemoveFile}
              aria-label={t('form-page.attachment-field.btn.remove-file')}
              data-testid={`${id}-remove-button`}
            >
              <DeleteIcon accessibility='decorative' />
            </button>
          </div>
        )}
      </div>

      <input
        type='hidden'
        {...register(id, {
          required: required
            ? t('form-page.attachment-field.error.required')
            : false,
          validate
        })}
      />

      {errorMessage && (
        <div className='inputMessage' id={`${id}-input-message`}>
          <div className='errorMessage'>{errorMessage}</div>
        </div>
      )}
    </div>
  )
}

export default AttachmentField
